import { canViewRouteForUser } from '../admin/permissions/permissionStore';
import { hrefForTicketingDepartment } from '../admin/permissions/permissionStore';
import { isPathHiddenByMenuSetting } from './menuHiddenRoutes';

export const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
export const SIDEBAR_EXPANDED_KEY = 'sidebarExpanded';
export const SIDEBAR_FAVORITES_KEY = 'sidebarFavorites';
export const MAX_SIDEBAR_FAVORITES = 5;

export const SECTION_HEADER_STYLES = {
  qms: [
    'border-l-blue-600 bg-blue-600/10 text-blue-800',
    'dark:border-l-blue-400 dark:bg-blue-500/20 dark:text-blue-100',
  ],
  it: [
    'border-l-teal-600 bg-teal-600/10 text-teal-900',
    'dark:border-l-teal-400 dark:bg-teal-500/20 dark:text-teal-100',
  ],
  operations: [
    'border-l-amber-600 bg-amber-600/10 text-amber-950',
    'dark:border-l-amber-400 dark:bg-amber-500/20 dark:text-amber-100',
  ],
  admin: [
    'border-l-violet-600 bg-violet-600/10 text-violet-900',
    'dark:border-l-violet-400 dark:bg-violet-500/20 dark:text-violet-100',
  ],
  default: [
    'border-l-slate-500 bg-slate-500/10 text-slate-800',
    'dark:border-l-slate-400 dark:bg-slate-500/15 dark:text-slate-200',
  ],
};

export const SECTION_COLLAPSED_STRIPE = {
  qms: 'bg-blue-600 dark:bg-blue-400',
  operations: 'bg-amber-600 dark:bg-amber-400',
  it: 'bg-teal-600 dark:bg-teal-400',
  admin: 'bg-violet-600 dark:bg-violet-400',
  default: 'bg-slate-400',
};

const HEADER_VARIANT_BY_NAME = {
  QMS: 'qms',
  Operations: 'operations',
  IT: 'it',
  Admin: 'admin',
};

export const SECTION_VARIANT_LABEL = {
  qms: 'QMS',
  operations: 'Operations',
  it: 'IT',
  admin: 'Admin',
};

/** Heroicon outline paths for breadcrumb section labels. */
export const SECTION_BREADCRUMB_ICONS = {
  qms:
    'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  it: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  operations:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  admin:
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  default: 'M4 6h16M4 12h16M4 18h16',
};

export const HOME_BREADCRUMB_ICON =
  'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';

export const DEFAULT_BREADCRUMB_MENU_ICON =
  'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z';

export function collapsedMenuTitle(item) {
  const section = SECTION_VARIANT_LABEL[item?._sectionVariant];
  return section ? `${section} · ${item.name}` : item.name;
}

export function headerVariantForItem(item) {
  if (item?.headerVariant) return item.headerVariant;
  const label = (item?.name || '').replace(/── | ──/g, '').trim();
  return HEADER_VARIANT_BY_NAME[label] || 'default';
}

function menuHrefsMatchingPathname(pathname, menuHrefs) {
  return (menuHrefs || []).filter(
    (h) => h && (pathname === h || pathname.startsWith(`${h}/`)),
  );
}

/**
 * True when `href` is the active sidebar link for `pathname`.
 * Uses the longest matching menu href so `/todo` is not active on `/todo/calendar`.
 * @param {string} href
 * @param {string} pathname
 * @param {string[]} [menuHrefs] — all visible menu hrefs; omit for prefix-only fallback
 */
export function pathnameMatchesHref(href, pathname, menuHrefs) {
  if (!href || pathname == null) return false;
  if (href === '/') return pathname === '/' || pathname === '';

  const matches = menuHrefsMatchingPathname(pathname, menuHrefs);
  if (matches.length > 0) {
    const best = matches.reduce((a, b) => (a.length >= b.length ? a : b));
    return href === best;
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Prefix match (e.g. expand parent when a deeper route is open). */
export function pathnameMatchesHrefPrefix(href, pathname) {
  if (!href || pathname == null) return false;
  if (href === '/') return pathname === '/' || pathname === '';
  return pathname === href || pathname.startsWith(`${href}/`);
}

function sortDepartments(depts) {
  return [...(depts || [])]
    .filter((d) => d.isActive !== false)
    .sort((a, b) => {
      const oa = a.record_order ?? 999999;
      const ob = b.record_order ?? 999999;
      if (oa !== ob) return oa - ob;
      return (a.name || '').localeCompare(b.name || '');
    });
}

function mergeTicketingOnItem(item, departments) {
  if (!item?.ticketingMergeDepartments) return item;
  const depts = sortDepartments(departments);
  const deptLinks = depts.map((d) => ({
    name: d.name || 'Department',
    href: hrefForTicketingDepartment(d.id),
  }));
  const rest = item.subitems || [];
  const subitems = deptLinks.length > 0 ? [...deptLinks, ...rest] : rest;
  return { ...item, subitems };
}

/** Recursively merges ticketing department links directly under Ticketing. */
export function mergeTicketingDepartmentsIntoMenu(baseMenuItems, departments) {
  return (baseMenuItems || []).map((item) => {
    let next = mergeTicketingOnItem(item, departments);
    if (next.subitems?.length) {
      next = {
        ...next,
        subitems: mergeTicketingDepartmentsIntoMenu(next.subitems, departments),
      };
    }
    return next;
  });
}

/**
 * Remove menu entries whose href (or all children) match menu.hiddenHrefs Application Setting.
 * @param {Array} items
 * @param {string[]} hiddenHrefs
 */
export function filterHiddenMenuRoutes(items, hiddenHrefs) {
  if (!hiddenHrefs?.length) return items || [];

  const filterList = (list) =>
    (list || [])
      .map((item) => {
        if (!item) return null;
        if (item.isHeader) return item;

        if (item.href && isPathHiddenByMenuSetting(item.href, hiddenHrefs)) {
          return null;
        }

        if (item.subitems?.length) {
          const subitems = filterList(item.subitems);
          if (subitems.length === 0 && !item.href) return null;
          return { ...item, subitems };
        }

        return item;
      })
      .filter(Boolean);

  return filterList(items);
}

/** merge ticketing → global hidden routes → user permissions → empty headers */
export function buildVisibleSidebarMenu(items, user, hiddenHrefs) {
  return pruneEmptySectionHeaders(
    filterSidebarMenuByRoutePermissions(filterHiddenMenuRoutes(items, hiddenHrefs), user),
  );
}

export function filterSidebarMenuByRoutePermissions(items, user) {
  return (items || [])
    .map((item) => {
      if (!item) return null;
      if (item.isHeader) return item;
      const filteredSubitems = item.subitems
        ? filterSidebarMenuByRoutePermissions(item.subitems, user)
        : undefined;
      const hasVisibleChildren =
        Array.isArray(filteredSubitems) && filteredSubitems.length > 0;
      const canViewSelf = item.href ? canViewRouteForUser(user, item.href) : false;
      if (!canViewSelf && !hasVisibleChildren) return null;
      return {
        ...item,
        ...(filteredSubitems ? { subitems: filteredSubitems } : {}),
      };
    })
    .filter(Boolean);
}

/** Remove section headers with no visible modules before the next header. */
export function pruneEmptySectionHeaders(items) {
  const result = [];
  for (let i = 0; i < (items || []).length; i++) {
    const item = items[i];
    if (!item?.isHeader) {
      result.push(item);
      continue;
    }
    let hasModule = false;
    for (let j = i + 1; j < items.length; j++) {
      if (items[j]?.isHeader) break;
      hasModule = true;
      break;
    }
    if (hasModule) result.push(item);
  }
  return result;
}

export function filterMenuBySearch(items, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return items;

  const filterList = (list) =>
    (list || [])
      .map((item) => {
        if (!item) return null;
        if (item.isHeader) return item;
        const nameMatch = item.name.toLowerCase().includes(q);
        const hrefMatch = (item.href || '').toLowerCase().includes(q);
        if (item.subitems?.length) {
          const filtered = filterList(item.subitems);
          if (filtered.length > 0) {
            return { ...item, subitems: filtered };
          }
          if (nameMatch || hrefMatch) {
            return { ...item, subitems: item.subitems };
          }
          return null;
        }
        return nameMatch || hrefMatch ? item : null;
      })
      .filter(Boolean);

  return pruneEmptySectionHeaders(filterList(items));
}

/** Expand keys for Sidebar `expandedItems` (e.g. `Approvals`, `Ticketing`). */
export function getExpandPathsForPathname(items, pathname, menuHrefs) {
  const out = {};
  const hrefs = menuHrefs ?? collectMenuHrefs(items);

  const visit = (list, parentPath = '') => {
    for (const item of list || []) {
      if (item.isHeader) continue;
      const fullPath = parentPath ? `${parentPath}.${item.name}` : item.name;
      const childHasMatch =
        item.href && pathnameMatchesHref(item.href, pathname, hrefs);
      let descendantMatch = false;
      if (item.subitems?.length) {
        visit(item.subitems, fullPath);
        descendantMatch = item.subitems.some((sub) => {
          const subPath = `${fullPath}.${sub.name}`;
          return out[subPath] || hasMatchInSubtree(sub, pathname, subPath, out, hrefs);
        });
      }
      if (childHasMatch || descendantMatch) {
        const parts = fullPath.split('.');
        let acc = '';
        for (let i = 0; i < parts.length; i++) {
          acc = acc ? `${acc}.${parts[i]}` : parts[i];
          out[acc] = true;
        }
      }
    }
  };

  visit(items);
  return out;
}

function hasMatchInSubtree(item, pathname, fullPath, out, menuHrefs) {
  if (item.href && pathnameMatchesHref(item.href, pathname, menuHrefs)) return true;
  if (!item.subitems) return false;
  return item.subitems.some((sub) =>
    hasMatchInSubtree(sub, pathname, `${fullPath}.${sub.name}`, out, menuHrefs),
  );
}

export function buildMenuRouteIndex(items) {
  const entries = [];
  let currentSection = '';
  let currentSectionVariant = 'default';
  let currentSectionIcon = SECTION_BREADCRUMB_ICONS.default;

  const walk = (list, ancestors = []) => {
    for (const item of list || []) {
      if (item.isHeader) {
        currentSection = (item.name || '').replace(/── | ──/g, '').trim();
        currentSectionVariant = headerVariantForItem(item);
        currentSectionIcon =
          SECTION_BREADCRUMB_ICONS[currentSectionVariant] ||
          SECTION_BREADCRUMB_ICONS.default;
        continue;
      }
      const node = { name: item.name, icon: item.icon || null };
      const chainTrail = [...ancestors, node];
      const chain = chainTrail.map((n) => n.name);
      if (item.href) {
        entries.push({
          href: item.href,
          chain,
          chainTrail,
          section: currentSection,
          sectionVariant: currentSectionVariant,
          sectionIcon: currentSectionIcon,
        });
      }
      if (item.subitems?.length) {
        walk(item.subitems, [...ancestors, { name: item.name, icon: item.icon || null }]);
      }
    }
  };

  walk(items);
  return entries;
}

export function breadcrumbSegmentsFromEntry(entry) {
  if (!entry) return null;

  const segments = [
    { label: 'Home', href: '/', icon: HOME_BREADCRUMB_ICON },
  ];

  if (entry.section) {
    segments.push({
      label: entry.section,
      icon:
        entry.sectionIcon ||
        SECTION_BREADCRUMB_ICONS[entry.sectionVariant] ||
        SECTION_BREADCRUMB_ICONS.default,
    });
  }

  const trail = entry.chainTrail || [];
  let lastParentIcon = null;
  trail.forEach((node, i) => {
    const isLast = i === trail.length - 1;
    const icon = node.icon || lastParentIcon || DEFAULT_BREADCRUMB_MENU_ICON;
    if (node.icon) lastParentIcon = node.icon;

    segments.push({
      label: node.name,
      icon,
      href: isLast ? entry.href : undefined,
      isCurrent: isLast,
    });
  });

  return segments;
}

export function findMenuBreadcrumb(index, pathname) {
  let best = null;
  for (const entry of index || []) {
    if (!pathnameMatchesHref(entry.href, pathname)) continue;
    if (!best || entry.href.length > best.href.length) {
      best = entry;
    }
  }
  if (!best) return null;
  return {
    section: best.section,
    chain: best.chain,
    segments: breadcrumbSegmentsFromEntry(best),
  };
}

/** Flat list of href + label for favorites picker. */
export function collectMenuLinks(items) {
  const links = [];
  const walk = (list, ancestors = []) => {
    for (const item of list || []) {
      if (item.isHeader || !item) continue;
      const chain = [...ancestors, item.name];
      if (item.href) {
        links.push({
          href: item.href,
          name: item.name,
          chain,
          icon: item.icon,
        });
      }
      if (item.subitems?.length) walk(item.subitems, chain);
    }
  };
  walk(items);
  return links;
}

export function collectMenuHrefs(items) {
  return collectMenuLinks(items).map((link) => link.href);
}

export function resolveFavoriteItems(allLinks, favoriteHrefs) {
  const set = new Set(favoriteHrefs || []);
  return (allLinks || []).filter((l) => set.has(l.href));
}

export function loadSidebarFavorites() {
  try {
    const raw = localStorage.getItem(SIDEBAR_FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string').slice(0, MAX_SIDEBAR_FAVORITES) : [];
  } catch {
    return [];
  }
}

export function saveSidebarFavorites(hrefs) {
  localStorage.setItem(
    SIDEBAR_FAVORITES_KEY,
    JSON.stringify((hrefs || []).slice(0, MAX_SIDEBAR_FAVORITES))
  );
}

export function loadSidebarExpanded() {
  try {
    const raw = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveSidebarExpanded(expanded) {
  try {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify(expanded || {}));
  } catch {
    /* ignore */
  }
}

/** Collapse one branch and all nested expand keys under it. */
export function collapseExpandBranch(expanded, itemPath) {
  const next = { ...(expanded || {}) };
  delete next[itemPath];
  const prefix = `${itemPath}.`;
  for (const key of Object.keys(next)) {
    if (key.startsWith(prefix)) delete next[key];
  }
  return next;
}

function getExpandSiblingKeys(expanded, itemPath) {
  const parts = itemPath.split('.');
  const depth = parts.length;
  const parentPrefix = parts.length > 1 ? `${parts.slice(0, -1).join('.')}.` : '';

  return Object.keys(expanded || {}).filter((key) => {
    if (!expanded[key] || key === itemPath) return false;
    const keyParts = key.split('.');
    if (keyParts.length !== depth) return false;
    if (depth === 1) return true;
    return key.startsWith(parentPrefix);
  });
}

/**
 * Toggle one menu branch; when opening, close sibling branches at the same level (accordion).
 */
export function toggleSidebarExpandedAccordion(expanded, itemPath) {
  if (expanded?.[itemPath]) {
    return collapseExpandBranch(expanded, itemPath);
  }

  let next = { ...(expanded || {}) };
  for (const sibling of getExpandSiblingKeys(expanded, itemPath)) {
    next = collapseExpandBranch(next, sibling);
  }

  const parts = itemPath.split('.');
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc}.${part}` : part;
    next[acc] = true;
  }
  return next;
}

/** Section variant for top-level modules (follows preceding header). */
export function annotateSectionsForFlatList(items) {
  let currentVariant = 'default';
  return (items || []).map((item) => {
    if (item.isHeader) {
      currentVariant = headerVariantForItem(item);
      return { ...item, _sectionVariant: currentVariant };
    }
    return { ...item, _sectionVariant: currentVariant };
  });
}
